import { queryOptions } from "@tanstack/react-query";

import { ProjectHeaderName } from "@/api/ApiClientProvider";
import { useApi } from "@/api/hooks/useApi";
import type {
  PortfolioBookingAnalyticsRequestDto,
  PortfolioCommunityFeedReportingRequestDto,
  PortfolioEventAnalyticsRequestDto,
  PortfolioTicketAnalyticsRequestDto,
} from "@/api/types";
import { commonAPIDataSelector } from "@/helpers/Network/selectors";
import { useProjectId } from "@/hooks/Network/useProjectId";
import type { BookingAnalyticsRequest } from "@/queries/analytics/booking-reporting-types";
import type { CommunityFeedAnalyticsRequest } from "@/queries/analytics/community-feed-reporting-types";
import type { EventAnalyticsRequest } from "@/queries/analytics/event-reporting-types";
import type { TicketAnalyticsRequest } from "@/queries/analytics/ticket-reporting-types";
import { QUERY_KEYS } from "@/query-keys";
import type { ApiQueryParams } from "@/types/api-types";

export type DateRange = "lastMonth" | "last6Months" | "lastYear";

export const useAnalyticsQueries = () => {
  const api = useApi();
  const currentProjectId = useProjectId();

  return {
    bookingReporting: (query: BookingAnalyticsRequest) =>
      queryOptions({
        queryKey: QUERY_KEYS.ANALYTICS_BOOKING_REPORTING(currentProjectId, query),
        queryFn: () => api.getAnalyticsBookingsReportingV2(query),
        select: commonAPIDataSelector,
      }),
    bookingSummary: () =>
      queryOptions({
        queryKey: [...QUERY_KEYS.ANALYTICS_BOOKING_REPORTING(currentProjectId, {}), "summary"],
        queryFn: () => api.getAnalyticsBookingsReportingSummaryV2({ dateRange: "last6Months" }),
        select: commonAPIDataSelector,
      }),
    portfolioBookingReporting: (query: PortfolioBookingAnalyticsRequestDto) =>
      queryOptions({
        queryKey: QUERY_KEYS.PORTFOLIO_ANALYTICS("bookings-reporting", query),
        queryFn: () => api.postPortfolioAnalyticsBookingsReportingV1(query),
        select: (response) => ({
          ...commonAPIDataSelector(response),
          assets: [],
          bookings: [],
          filterOptions: { assets: [] },
        }),
      }),
    communityFeedReporting: (query: CommunityFeedAnalyticsRequest) =>
      queryOptions({
        queryKey: QUERY_KEYS.ANALYTICS_COMMUNITY_FEED_REPORTING(currentProjectId, query),
        queryFn: () => api.getAnalyticsCommunityFeedReportingV2(query),
        select: commonAPIDataSelector,
      }),
    communityFeedSummary: () =>
      queryOptions({
        queryKey: [...QUERY_KEYS.ANALYTICS_COMMUNITY_FEED_REPORTING(currentProjectId, {}), "summary"],
        queryFn: () => api.getAnalyticsCommunityFeedReportingSummaryV2({ dateRange: "last6Months" }),
        select: commonAPIDataSelector,
      }),
    portfolioCommunityFeedReporting: (query: PortfolioCommunityFeedReportingRequestDto) =>
      queryOptions({
        queryKey: QUERY_KEYS.PORTFOLIO_ANALYTICS("community-feed-reporting", query),
        queryFn: () => api.postPortfolioAnalyticsCommunityFeedReportingV1(query),
        select: commonAPIDataSelector,
      }),
    eventReporting: (query: EventAnalyticsRequest) =>
      queryOptions({
        queryKey: QUERY_KEYS.ANALYTICS_EVENT_REPORTING(currentProjectId, query),
        queryFn: () => api.getAnalyticsEventsReportingV2(query),
        select: commonAPIDataSelector,
      }),
    eventSummary: () =>
      queryOptions({
        queryKey: [...QUERY_KEYS.ANALYTICS_EVENT_REPORTING(currentProjectId, {}), "summary"],
        queryFn: () => api.getAnalyticsEventsReportingSummaryV2({ dateRange: "last6Months" }),
        select: commonAPIDataSelector,
      }),
    portfolioEventReporting: (query: PortfolioEventAnalyticsRequestDto) =>
      queryOptions({
        queryKey: QUERY_KEYS.PORTFOLIO_ANALYTICS("events-reporting", query),
        queryFn: () => api.postPortfolioAnalyticsEventsReportingV1(query),
        select: (response) => {
          const data = commonAPIDataSelector(response);

          return {
            ...data,
            events: [],
            recurring: { ...data.recurring, series: [] },
          };
        },
      }),
    ticketReporting: (query: TicketAnalyticsRequest) =>
      queryOptions({
        queryKey: QUERY_KEYS.ANALYTICS_TICKET_REPORTING(currentProjectId, query),
        queryFn: () => api.getAnalyticsTicketsReportingV2(query),
        select: commonAPIDataSelector,
      }),
    ticketSummary: () =>
      queryOptions({
        queryKey: [...QUERY_KEYS.ANALYTICS_TICKET_REPORTING(currentProjectId, {}), "summary"],
        queryFn: () => api.getAnalyticsTicketsReportingSummaryV2({ dateRange: "last6Months" }),
        select: commonAPIDataSelector,
      }),
    portfolioTicketReporting: (query: PortfolioTicketAnalyticsRequestDto) =>
      queryOptions({
        queryKey: QUERY_KEYS.PORTFOLIO_ANALYTICS("tickets-reporting", query),
        queryFn: () => api.postPortfolioAnalyticsTicketsReportingV1(query),
        select: (response) => ({ ...commonAPIDataSelector(response), tickets: [] }),
      }),
    portfolioTicketSummary: (query: PortfolioTicketAnalyticsRequestDto) =>
      queryOptions({
        queryKey: [...QUERY_KEYS.PORTFOLIO_ANALYTICS("tickets-reporting", query), "summary"],
        queryFn: () => api.postPortfolioAnalyticsTicketsReportingSummaryV1(query),
        select: commonAPIDataSelector,
      }),
    fah: (projectId?: string) =>
      queryOptions({
        queryKey: QUERY_KEYS.ANALYTICS_FEELING_AT_HOME(projectId || currentProjectId),
        queryFn: () =>
          api.getAnalyticsFeelingAtHomeV1({
            headers: {
              [ProjectHeaderName]: projectId || currentProjectId,
            },
          }),
        select: commonAPIDataSelector,
        staleTime: Infinity,
        gcTime: Infinity,
      }),
    fahMonthlyStats: (dateRange: DateRange, projectId?: string) =>
      queryOptions({
        queryKey: QUERY_KEYS.ANALYTICS_FEELING_AT_HOME_MONTHLY(projectId || currentProjectId, dateRange),
        queryFn: () =>
          api.getAnalyticsFeelingAtHomeMonthlyV1(
            { dateRange },
            {
              headers: {
                [ProjectHeaderName]: projectId || currentProjectId,
              },
            },
          ),
        select: commonAPIDataSelector,
        staleTime: Infinity,
        gcTime: Infinity,
      }),
    fahDetails: (query: ApiQueryParams<"getAnalyticsFeelingAtHomeDetailsV2">) =>
      queryOptions({
        queryKey: QUERY_KEYS.ANALYTICS_FEELING_AT_HOME_DETAILS(currentProjectId, query),
        queryFn: () => api.getAnalyticsFeelingAtHomeDetailsV2(query),
        select: commonAPIDataSelector,
      }),
    fahSuggestions: () =>
      queryOptions({
        queryKey: QUERY_KEYS.ANALYTICS_FEELING_AT_HOME_SUGGESTIONS(currentProjectId),
        queryFn: () => api.getAnalyticsFeelingAtHomeSuggestionsV1(),
        select: commonAPIDataSelector,
        staleTime: Infinity,
        gcTime: Infinity,
      }),
    fahHistoryDetails: (params: ApiQueryParams<"getAnalyticsFeelingAtHomeDetailsV2">) =>
      queryOptions({
        queryKey: QUERY_KEYS.FEELING_AT_HOME_HISTORY_DETAILS(currentProjectId, params),
        queryFn: () => api.getAnalyticsFeelingAtHomeDetailsV2(params),
        select: commonAPIDataSelector,
      }),
    fahAiQuestionSummary: ({
      questionId,
      startDate,
      endDate,
    }: {
      questionId: string;
      startDate?: string;
      endDate?: string;
    }) =>
      queryOptions({
        queryKey: QUERY_KEYS.FEELING_AT_HOME_HISTORY_QUESTION_RESPONSE_SUMMARY(currentProjectId, questionId, {
          startDate,
          endDate,
        }),
        queryFn: () =>
          api.getAnalyticsFeelingAtHomeDetailsQuestionsSummaryV2(questionId, {
            startDate,
            endDate,
          }),
        select: commonAPIDataSelector,
        staleTime: 1000 * 60 * 5,
      }),
    fahExport: (query: ApiQueryParams<"getAnalyticsFeelingAtHomeExportV2">) => ({
      queryKey: QUERY_KEYS.FEELING_AT_HOME_EXPORT(currentProjectId, query),
      queryFn: () => api.getAnalyticsFeelingAtHomeExportV2(query).then((x) => x.blob()),
    }),
    communityFeedStats: () =>
      queryOptions({
        queryKey: QUERY_KEYS.ANALYTICS_COMMUNITY_FEED_STATS(currentProjectId),
        queryFn: () => api.getAnalyticsCommunityFeedV1(),
        select: commonAPIDataSelector,
        staleTime: Infinity,
        gcTime: Infinity,
      }),
    contribution: (dateRange: DateRange, query?: { withCurrentMonth: boolean }) =>
      queryOptions({
        queryKey: QUERY_KEYS.ANALYTICS_ACTIVE_CONTRIBUTORS(currentProjectId, dateRange, query),
        queryFn: () => api.getAnalyticsEngagedResidentsMonthlyV2({ dateRange, ...query }),
        select: commonAPIDataSelector,
      }),
    adoption: () =>
      queryOptions({
        queryKey: QUERY_KEYS.ANALYTICS_ADOPTION(currentProjectId),
        queryFn: () => api.getAnalyticsAdoptionRateV1(),
        select: commonAPIDataSelector,
      }),
    companyAdoption: () =>
      queryOptions({
        queryKey: QUERY_KEYS.ANALYTICS_ADOPTION(currentProjectId),
        queryFn: () => api.getAnalyticsCompanyAdoptionRateV1(),
        select: commonAPIDataSelector,
      }),
    reachDetails: (dateRange: DateRange) =>
      queryOptions({
        queryKey: QUERY_KEYS.ANALYTICS_DETAILS_REACH(currentProjectId, dateRange),
        queryFn: () => api.getAnalyticsAdoptionRateDetailsDetailsV2("reach", { dateRange }),
        select: commonAPIDataSelector,
      }),
    adoptionDetails: (dateRange: DateRange) =>
      queryOptions({
        queryKey: QUERY_KEYS.ANALYTICS_DETAILS_ADOPTION(currentProjectId, dateRange),
        queryFn: () => api.getAnalyticsAdoptionRateDetailsDetailsV2("adoption", { dateRange }),
        select: commonAPIDataSelector,
      }),
    engagement: () =>
      queryOptions({
        queryKey: QUERY_KEYS.ANALYTICS_ENGAGED_RESIDENTS(currentProjectId),
        queryFn: () => api.getAnalyticsResidentEngagementV1(),
        select: commonAPIDataSelector,
      }),
    engagementsDetails: (dateRange: DateRange) =>
      queryOptions({
        queryKey: QUERY_KEYS.ANALYTICS_DETAILS_ENGAGEMENT(currentProjectId, dateRange),
        queryFn: () => api.getAnalyticsResidentEngagementDetailsV2({ dateRange }),
        select: commonAPIDataSelector,
      }),
    vacantAddresses: (dateRange: DateRange) =>
      queryOptions({
        queryKey: QUERY_KEYS.ANALYTICS_DETAILS_ADOPTION_VACANT(currentProjectId, dateRange),
        queryFn: () => api.getAnalyticsAdoptionRateDetailsDetailsV2("vacant", { dateRange }),
        select: commonAPIDataSelector,
      }),
    widgets: () =>
      queryOptions({
        queryKey: QUERY_KEYS.DATA_WIDGETS_LIST(currentProjectId),
        queryFn: () => api.getWidgetsActiveV1(),
        select: commonAPIDataSelector,
      }),
    askForHelpStats: (dateRange: DateRange) =>
      queryOptions({
        queryKey: QUERY_KEYS.ANALYTICS_ASK_FOR_HELP_STATS(currentProjectId, dateRange),
        queryFn: () => api.getAnalyticsAskForHelpMonthlyV1({ dateRange }),
        select: commonAPIDataSelector,
      }),
    weeklyMessages: () =>
      queryOptions({
        queryKey: QUERY_KEYS.ANALYTICS_WEEKLY_MESSAGES(currentProjectId),
        queryFn: () => api.getAnalyticsWeeklyMessagesV1(),
        select: commonAPIDataSelector,
      }),
    conversationsMonthly: (dateRange: DateRange) =>
      queryOptions({
        queryKey: QUERY_KEYS.ANALYTICS_CONVERSATIONS_MONTHLY(currentProjectId, dateRange),
        queryFn: () => api.getAnalyticsConversationsMonthlyV1({ dateRange }),
        select: commonAPIDataSelector,
      }),
    messageLabelStats: (dateRange: DateRange) =>
      queryOptions({
        queryKey: QUERY_KEYS.ANALYTICS_MESSAGE_LABEL_STATS(currentProjectId, dateRange),
        queryFn: () => api.getAnalyticsMessageLabelStatsV1({ dateRange }),
        select: commonAPIDataSelector,
      }),
    bucketTicketLeadTimeMonthly: (dateRange: DateRange) =>
      queryOptions({
        queryKey: QUERY_KEYS.ANALYTICS_TICKETS_BUCKETS_LEAD_TIME(currentProjectId, dateRange),
        queryFn: () => api.getAnalyticsBucketTicketLeadTimeMonthlyV1({ dateRange }),
        select: (response) => [...commonAPIDataSelector(response).data].reverse(),
      }),
    ticketLeadTimeMonthly: (dateRange: DateRange) =>
      queryOptions({
        queryKey: QUERY_KEYS.ANALYTICS_TICKET_LEAD_TIME_MONTHLY(currentProjectId, dateRange),
        queryFn: () => api.getAnalyticsTicketLeadTimeMonthlyV1({ dateRange }),
        select: commonAPIDataSelector,
      }),
    ticketOverviewMonthly: (dateRange: DateRange) =>
      queryOptions({
        queryKey: QUERY_KEYS.ANALYTICS_TICKETS_OVERVIEW(currentProjectId, dateRange),
        queryFn: () => api.getAnalyticsTicketOverviewMonthlyV1({ dateRange }),
        select: (response) => {
          const { data, benchmark } = commonAPIDataSelector(response);

          return {
            data: [...data].reverse(),
            benchmark: [...benchmark].reverse(),
          };
        },
      }),
    ticketRatingMonthly: (dateRange: DateRange) =>
      queryOptions({
        queryKey: QUERY_KEYS.ANALYTICS_TICKETS_AVERAGE_RATING(currentProjectId, dateRange),
        queryFn: () => api.getAnalyticsTicketRatingMonthlyV1({ dateRange }),
        select: commonAPIDataSelector,
      }),
  };
};
